import {
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import useCustomQuery from "../../hooks/useCustomQuery";
import {
    isMystudioV4Configured,
    mystudioV4,
    shouldRetryMystudioV4,
} from "./client";
import {
    adaptActionItems,
    adaptHome,
    adaptGalleryPost,
    adaptGalleryPosts,
    adaptGallerySection,
    adaptInquiries,
    adaptInquiry,
    adaptMyPage,
    adaptNotificationPreferences,
    adaptNotifications,
    adaptPhotoAssets,
    adaptPhotoDeliveries,
    adaptProduct,
    adaptProducts,
    adaptPublicContent,
    adaptReviewEventCatalog,
    adaptSchedule,
    adaptSchedules,
    adaptThemes,
    adaptTerms,
} from "./adapters";
import {
    ApplyMystudioReviewEventInput,
    CreateMystudioInquiryInput,
    CreateProductDraftInput,
    MystudioApiEnvelope,
    SubmitThemeSelectionInput,
    UpdateMystudioNotificationPreferencesInput,
    UpdateMystudioShippingInput,
} from "./types";
import {
    V4AvailabilityDto,
    V4PageResultDto,
    V4PhotoAssetDto,
    V4ThemeDto,
} from "./dto";
import {
    createDemoGalleryPostPage,
    createDemoGallerySections,
    createDemoInquiryPage,
    createDemoNotificationPage,
    createDemoPhotoAssetPage,
    createDemoPublicContent,
    enrichDemoHome,
    enrichDemoPhotoDeliveries,
    enrichDemoProducts,
    enrichDemoSchedules,
    enrichDemoThemes,
    getDemoGalleryPost,
    getDemoInquiry,
    isDemoResourceId,
} from "./demoContent";

const V4_STALE_TIME = 30_000;
const THEME_PAGE_SIZE = 100;
const MAX_THEME_PAGES = 20;
const PHOTO_ASSET_PAGE_SIZE = 100;
const MAX_PHOTO_ASSET_PAGES = 20;

function demoResponse<T>(data: T): MystudioApiEnvelope<T> {
    return {
        data,
        meta: { source: "preview" as const },
    };
}

export async function loadMystudioThemesV4(
    query: Record<string, unknown> = {},
    signal?: AbortSignal,
) {
    const firstResponse = await mystudioV4.themes({
        ...query,
        page: 1,
        limit: THEME_PAGE_SIZE,
    }, signal);
    if (Array.isArray(firstResponse.data)) return firstResponse;

    const firstPage = firstResponse.data as V4PageResultDto<V4ThemeDto>;
    const items = [...(firstPage.items || [])];
    let currentPage = Number(firstPage.page) || 1;
    let hasNext = Boolean(firstPage.hasNext);

    while (hasNext && currentPage < MAX_THEME_PAGES) {
        currentPage += 1;
        const nextResponse = await mystudioV4.themes({
            ...query,
            page: currentPage,
            limit: THEME_PAGE_SIZE,
        }, signal);
        if (Array.isArray(nextResponse.data)) {
            items.push(...nextResponse.data);
            hasNext = false;
            break;
        }

        const nextPage = nextResponse.data as V4PageResultDto<V4ThemeDto>;
        items.push(...(nextPage.items || []));
        hasNext = Boolean(nextPage.hasNext);
    }

    const data: V4AvailabilityDto<V4ThemeDto> & V4PageResultDto<V4ThemeDto> = {
        ...firstPage,
        items,
        page: 1,
        limit: items.length,
        total: Number(firstPage.total) || items.length,
        hasNext,
    };
    return { ...firstResponse, data };
}

export async function loadMystudioPhotoDeliveryAssetsV4(
    deliveryId: string,
    signal?: AbortSignal,
) {
    const firstResponse = await mystudioV4.photoDeliveryAssets(
        deliveryId,
        { page: 1, limit: PHOTO_ASSET_PAGE_SIZE },
        signal,
    );
    if (Array.isArray(firstResponse.data)) return firstResponse;

    const firstPage = firstResponse.data as V4PageResultDto<V4PhotoAssetDto>;
    const items = [...(firstPage.items || [])];
    let currentPage = Number(firstPage.page) || 1;
    let hasNext = Boolean(firstPage.hasNext);

    while (hasNext && currentPage < MAX_PHOTO_ASSET_PAGES) {
        currentPage += 1;
        const nextResponse = await mystudioV4.photoDeliveryAssets(
            deliveryId,
            { page: currentPage, limit: PHOTO_ASSET_PAGE_SIZE },
            signal,
        );
        if (Array.isArray(nextResponse.data)) {
            items.push(...nextResponse.data);
            hasNext = false;
            break;
        }

        const nextPage = nextResponse.data as V4PageResultDto<V4PhotoAssetDto>;
        items.push(...(nextPage.items || []));
        hasNext = Boolean(nextPage.hasNext);
    }

    const data: V4AvailabilityDto<V4PhotoAssetDto> &
        V4PageResultDto<V4PhotoAssetDto> = {
        ...firstPage,
        items,
        page: 1,
        limit: items.length,
        total: Number(firstPage.total) || items.length,
        hasNext,
    };
    return { ...firstResponse, data };
}

function configuredQuery<T>({
    queryKey,
    queryFn,
    enabled = true,
}: {
    queryKey: readonly unknown[];
    queryFn: ({ signal }: { signal: AbortSignal }) => Promise<T>;
    enabled?: boolean;
}) {
    const configured = isMystudioV4Configured();
    const query = useCustomQuery({
        queryKey,
        queryFn,
        enabled: configured && enabled,
        retry: shouldRetryMystudioV4,
        staleTime: V4_STALE_TIME,
    });
    return { ...query, configured };
}

export function useMystudioHomeV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "home"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.home(signal);
            return { ...response, data: enrichDemoHome(adaptHome(response.data)) };
        },
    });
}

export function useMystudioGalleryPostsV4({
    page = 1,
    limit = 20,
    categoryId,
    sort = "latest",
}: {
    page?: number;
    limit?: number;
    categoryId?: string;
    sort?: "latest" | "popular";
} = {}) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "gallery-posts", page, limit, categoryId, sort],
        queryFn: async ({ signal }) => {
            const demoPage = createDemoGalleryPostPage({ categoryId, sort });
            if (demoPage) return demoResponse(demoPage);
            const response = await mystudioV4.galleryPosts({
                page,
                limit,
                categoryId,
                sort,
            }, signal);
            return { ...response, data: adaptGalleryPosts(response.data) };
        },
    });
}

export function useMystudioGallerySectionsV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "gallery-sections"],
        queryFn: async ({ signal }) => {
            const demoSections = createDemoGallerySections();
            if (demoSections) return demoResponse(demoSections);
            const response = await mystudioV4.gallerySections(signal);
            return {
                ...response,
                data: Array.isArray(response.data)
                    ? response.data.map(adaptGallerySection)
                    : [],
            };
        },
    });
}

export function useMystudioGalleryPostV4(postId?: string) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "gallery-post", postId],
        enabled: Boolean(postId),
        queryFn: async ({ signal }) => {
            const demoPost = getDemoGalleryPost(postId);
            if (demoPost) return demoResponse(demoPost);
            const response = await mystudioV4.galleryPost(
                postId || "",
                { page: 1, limit: 100 },
                signal,
            );
            return { ...response, data: adaptGalleryPost(response.data) };
        },
    });
}

export function useRegisterMystudioGalleryViewV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (postId: string) => isDemoResourceId(postId)
            ? Promise.resolve(demoResponse({ viewCount: 0 }))
            : mystudioV4.registerGalleryPostView(postId),
        onSuccess: (_response, postId) => {
            queryClient.invalidateQueries({
                queryKey: ["mystudio-v4", "gallery-post", postId],
            });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "gallery-posts"] });
        },
    });
}

export function useMystudioPublicContentV4(query: Record<string, unknown> = {}) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "public-content", query],
        queryFn: async ({ signal }) => {
            const demoContent = createDemoPublicContent();
            if (demoContent) return demoResponse(demoContent);
            const response = await mystudioV4.publicContent(query, signal);
            return { ...response, data: adaptPublicContent(response.data) };
        },
    });
}

export function useApplyPublicEventV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ contentId, message }: { contentId: string; message?: string }) =>
            isDemoResourceId(contentId)
                ? Promise.resolve(demoResponse({ applied: true }))
                : mystudioV4.applyPublicEvent(contentId, message),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "public-content"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useMystudioSchedulesV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "schedules"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.schedules(signal);
            return { ...response, data: enrichDemoSchedules(adaptSchedules(response.data)) };
        },
    });
}

export function useMystudioThemesV4(query: Record<string, unknown> = {}) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "themes", query],
        queryFn: async ({ signal }) => {
            const response = await loadMystudioThemesV4(query, signal);
            return { ...response, data: enrichDemoThemes(adaptThemes(response.data)) };
        },
    });
}

export function useMystudioProductsV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "products"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.products(signal);
            return { ...response, data: enrichDemoProducts(adaptProducts(response.data)) };
        },
    });
}

export function useMystudioPhotoDeliveriesV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "photo-deliveries"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.photoDeliveries(signal);
            return {
                ...response,
                data: enrichDemoPhotoDeliveries(adaptPhotoDeliveries(response.data)),
            };
        },
    });
}

export function useMystudioPhotoDeliveryAssetsV4(deliveryId: string) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "photo-deliveries", deliveryId, "assets"],
        enabled: Boolean(deliveryId),
        queryFn: async ({ signal }) => {
            const demoPage = createDemoPhotoAssetPage(deliveryId);
            if (demoPage) return demoResponse(demoPage);
            const response = await loadMystudioPhotoDeliveryAssetsV4(
                deliveryId,
                signal,
            );
            return { ...response, data: adaptPhotoAssets(response.data) };
        },
    });
}

export function useMystudioPhotoDownloadTicketV4() {
    return useMutation({
        mutationFn: (deliveryId: string) =>
            mystudioV4.createPhotoDownloadTicket(deliveryId),
    });
}

export function useMystudioMyPageV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "my-page"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.myPage(signal);
            return { ...response, data: adaptMyPage(response.data) };
        },
    });
}

export function useMystudioActionItemsV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "action-items"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.actionItems(signal);
            return { ...response, data: adaptActionItems(response.data) };
        },
    });
}

export function useMystudioTermsV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "terms"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.terms(signal);
            return { ...response, data: adaptTerms(response.data) };
        },
    });
}

export function useMystudioInquiriesV4(query: Record<string, unknown> = {}) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "inquiries", query],
        queryFn: async ({ signal }) => {
            const demoPage = createDemoInquiryPage();
            if (demoPage) return demoResponse(demoPage);
            const response = await mystudioV4.inquiries(query, signal);
            return { ...response, data: adaptInquiries(response.data) };
        },
    });
}

export function useMystudioInquiryV4(requestId?: string) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "inquiries", requestId],
        enabled: Boolean(requestId),
        queryFn: async ({ signal }) => {
            const demoInquiry = getDemoInquiry(requestId);
            if (demoInquiry) return demoResponse(demoInquiry);
            const response = await mystudioV4.inquiry(requestId || "", signal);
            return { ...response, data: adaptInquiry(response.data) };
        },
    });
}

export function useMystudioNotificationsV4(
    query: Record<string, unknown> = {},
    enabled = true,
) {
    return configuredQuery({
        queryKey: ["mystudio-v4", "notifications", query],
        enabled,
        queryFn: async ({ signal }) => {
            const demoPage = createDemoNotificationPage();
            if (demoPage) return demoResponse(demoPage);
            const response = await mystudioV4.notifications(query, signal);
            return { ...response, data: adaptNotifications(response.data) };
        },
    });
}

export function useMystudioNotificationPreferencesV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "notification-preferences"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.notificationPreferences(signal);
            return {
                ...response,
                data: adaptNotificationPreferences(response.data),
            };
        },
    });
}

export function useUpdateNotificationPreferencesV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: UpdateMystudioNotificationPreferencesInput) => {
            const response = await mystudioV4.updateNotificationPreferences(input);
            return {
                ...response,
                data: adaptNotificationPreferences(response.data),
            };
        },
        onSuccess: () => queryClient.invalidateQueries({
            queryKey: ["mystudio-v4", "notification-preferences"],
        }),
    });
}

export function useCreateInquiryV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateMystudioInquiryInput) => {
            const response = await mystudioV4.createInquiry(input);
            return { ...response, data: adaptInquiry(response.data) };
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "inquiries"] }),
    });
}

export function useAddInquiryMessageV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ requestId, message }: { requestId: string; message: string }) =>
            mystudioV4.addInquiryMessage(requestId, message),
        onSuccess: (_response, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["mystudio-v4", "inquiries", variables.requestId],
            });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "inquiries"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useMarkInquiryReadV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (requestId: string) => isDemoResourceId(requestId)
            ? Promise.resolve(demoResponse({ requestId: 0, updatedCount: 1 }))
            : mystudioV4.markInquiryRead(requestId),
        onSuccess: (_response, requestId) => {
            queryClient.invalidateQueries({
                queryKey: ["mystudio-v4", "inquiries", requestId],
            });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "inquiries"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useToggleThemeFavoriteV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ themeId, favorite }: { themeId: string; favorite: boolean }) =>
            mystudioV4.toggleThemeFavorite(themeId, favorite),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "themes"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useSubmitThemeSelectionV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            scheduleId,
            input,
        }: {
            scheduleId: string;
            input: SubmitThemeSelectionInput;
        }) => mystudioV4.submitThemeSelection(scheduleId, input).then((response) => ({
            ...response,
            data: adaptSchedule(response.data),
        })),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "schedules"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useApplyReviewEventV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            reviewEventId,
            input,
        }: {
            reviewEventId: string;
            input: ApplyMystudioReviewEventInput;
        }) => mystudioV4.applyReviewEvent(reviewEventId, input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "schedules"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useMystudioReviewEventsV4() {
    return configuredQuery({
        queryKey: ["mystudio-v4", "review-events"],
        queryFn: async ({ signal }) => {
            const response = await mystudioV4.reviewEvents(signal);
            return {
                ...response,
                data: adaptReviewEventCatalog(response.data),
            };
        },
    });
}

export function useCreateProductDraftV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateProductDraftInput) =>
            mystudioV4.createProductDraft(input),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "products"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useReplaceProductPhotoSelectionV4() {
    return useMutation({
        mutationFn: ({ requestId, assetIds }: { requestId: string; assetIds: number[] }) =>
            mystudioV4.replaceProductPhotoSelection(requestId, assetIds),
    });
}

export function useSubmitProductRequestV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (requestId: string) => mystudioV4.submitProductRequest(requestId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "products"] });
            queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "home"] });
        },
    });
}

export function useUpdateShippingV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: UpdateMystudioShippingInput) => mystudioV4.updateShipping(input),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "my-page"] }),
    });
}

export function useMarkNotificationReadV4() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (notificationId: string) =>
            mystudioV4.markNotificationRead(notificationId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["mystudio-v4", "notifications"] }),
    });
}
